/**
 * Identity Service API
 * Authentication and user management endpoints
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.IDENTITY.baseURL, API_CONFIG.IDENTITY.timeout)

export const identityService = {
  // ==================== Authentication ====================
  
  /**
   * Login user
   * @param {object} credentials - {email, password}
   * @returns {Promise} Token and user data
   */
  login: async (credentials) => {
    const response = await client.post('/auth/login', {
      email: credentials.email,
      password: credentials.password
    })
    return response.data
  },

  /**
   * Logout and revoke token
   * @returns {Promise}
   */
  logout: async () => {
    const response = await client.post('/auth/logout')
    return response.data
  },

  /**
   * Validate token
   * @returns {Promise} Token validity status
   */
  validateToken: async () => {
    const response = await client.get('/auth/verify-token')
    return response.data
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise}
   */
  requestPasswordReset: async (email) => {
    const response = await client.post('/auth/request-reset', { email })
    return response.data
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise}
   */
  resetPassword: async (token, newPassword) => {
    const response = await client.post('/auth/reset-password', {
      token,
      new_password: newPassword
    })
    return response.data
  },

  // ==================== Tenants (System Admin Only) ====================

  /**
   * Get all tenants
   */
  getTenants: async () => {
    const response = await client.get('/tenants/')
    return response.data
  },

  /**
   * Create a new tenant
   */
  createTenant: async (tenantData) => {
    const response = await client.post('/tenants/', tenantData)
    return response.data
  },

  /**
   * Update tenant branding
   */
  /**
   * Set (or clear) the COMPANY'S language — the default every screen opens in, including the
   * sign-in page, which is the only rung of the language chain that can reach a visitor who has not
   * signed in yet. `null` clears it and hands that page back to the visitor's own device.
   *
   * Sent as an explicit key so the server can tell "clear it" from "leave it alone" — the branding
   * endpoint reads `model_fields_set`, not `is not None`, for exactly this field.
   */
  setTenantDefaultLanguage: async (tenantId, language) => {
    const response = await client.put(`/branding/${tenantId}`, { default_language: language || null })
    return response.data
  },

  updateBranding: async (tenantId, brandingData) => {
    const response = await client.put(`/branding/${tenantId}`, brandingData)
    return response.data
  },

  /**
   * Which contract types automatically email the customer a copy of their signed field ticket.
   *
   * Returns `{ settings, labels, wired, unwired_reason }`. The labels and the wired map come from
   * the SERVER rather than being hardcoded here: the switch keys are derived from the contract-type
   * vocabulary in Python, so a contract type added there gets a switch without anyone remembering
   * to edit this file — and `wired` is what lets the screen say a switch is stored but not yet
   * acted on, rather than showing a control that silently does nothing.
   */
  getFieldTicketEmailSettings: async (tenantId) => {
    const response = await client.get(`/branding/${tenantId}/field-ticket-emails`)
    return response.data
  },

  /**
   * Save them. WHOLE DOCUMENT, not a partial merge — these are checkboxes submitted together, and a
   * merge would make an unticked box indistinguishable from a box that was not on the form, so
   * turning one OFF would silently do nothing.
   */
  updateFieldTicketEmailSettings: async (tenantId, settings) => {
    const response = await client.put(`/branding/${tenantId}/field-ticket-emails`, { settings })
    return response.data
  },

  /**
   * Where a customer's reply to a document email goes.
   *
   * Returns `{ reply_to, fallback_note, takes_effect_note }`. The two notes come from the SERVER
   * rather than being written into the component, so the screen cannot claim something different
   * from what the backend actually does when the value is empty.
   */
  getReplyTo: async (tenantId) => {
    const response = await client.get(`/branding/${tenantId}/reply-to`)
    return response.data
  },

  /**
   * Save it. An empty string CLEARS it, which is a real choice and not the same as not submitting:
   * a company that loses the shared mailbox it nominated needs to be able to say so, and an omitted
   * Reply-To header is better than one pointing at a mailbox that bounces.
   *
   * A no-reply address is rejected by the server with a message written for a non-technical admin —
   * surface `detail` rather than a generic string.
   */
  updateReplyTo: async (tenantId, replyTo) => {
    const response = await client.put(`/branding/${tenantId}/reply-to`, { reply_to: replyTo || '' })
    return response.data
  },

  /**
   * The company's own consent to the AI Office Worker, its notice and the facts it keeps from the AI.
   *
   * Returns `{ entitled, consented, consent_version, consent_changed_at, free_text_opt_in,
   * field_opt_outs, optable_fields, notice_version, notice }`. `entitled` is the paid add-on, which
   * only the platform changes; everything else is the company admin's.
   */
  getAiSettings: async (tenantId) => {
    const response = await client.get(`/ai-settings/${tenantId}`)
    return response.data
  },

  /**
   * Save the admin's choice. `notice_version` must be the version the admin was SHOWN: the server
   * refuses (409) if the notice changed since, so nobody consents to words they did not read.
   */
  updateAiSettings: async (tenantId, { consented, notice_version, free_text_opt_in, field_opt_outs }) => {
    const response = await client.put(`/ai-settings/${tenantId}`, {
      consented, notice_version, free_text_opt_in: Boolean(free_text_opt_in), field_opt_outs: field_opt_outs || [],
    })
    return response.data
  },

  /**
   * PLATFORM ADMIN ONLY. Every company, which held modules (Capital, Capex, Invoicing) its company
   * switch turns on, and which it is actually served once the environment has its say.
   *
   * Returns `{ environment, labels, companies: [{ tenant_id, name, is_active, modules, effective }] }`.
   * The labels come from the server (shared/module_switches.py SWITCHES), so a module added there is
   * offered here without an edit to this file.
   */
  getCompanyModules: async () => {
    const response = await client.get('/modules/switches/companies')
    return response.data
  },

  /**
   * PLATFORM ADMIN ONLY. Turn held modules on or off for ONE company — a PARTIAL change: the modules
   * named are set, the rest keep their value. `note` is kept on the audit row. Every change is audited
   * server-side (MODULE_SWITCH_CHANGE) in the same transaction as the change.
   */
  setCompanyModules: async (tenantId, modules, note) => {
    const response = await client.put(`/modules/switches/companies/${tenantId}`, { modules, note: note || null })
    return response.data
  },

  /**
   * Change password (authenticated user)
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise}
   */
  changePassword: async (currentPassword, newPassword) => {
    const response = await client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  },

  /**
   * Admin set password for a user (master admin only)
   * @param {string} userId - Target user ID
   * @param {string} newPassword - New password to set
   * @returns {Promise}
   */
  adminSetPassword: async (userId, newPassword) => {
    const response = await client.post(`/user/${userId}/set-password`, {
      new_password: newPassword
    })
    return response.data
  },

  // ==================== User Management ====================

  /**
   * Get current user profile
   * @returns {Promise} User data
   */
  getProfile: async () => {
    const response = await client.get('/user/me')
    return response.data
  },

  /**
   * Update user profile
   * @param {object} profileData - Profile fields to update
   * @returns {Promise} Updated user data
   */
  updateProfile: async (profileData) => {
    const response = await client.put('/user/me', profileData)
    return response.data
  },

  /**
   * Register new user (admin only)
   * @param {object} userData - User registration data
   * @returns {Promise} Created user data
   */
  register: async (userData) => {
    const response = await client.post('/user/register', userData)
    return response.data
  },

  /**
   * Get all users (admin only)
   * @returns {Promise} Array of users
   */
  getUsers: async () => {
    const response = await client.get('/user/')
    return response.data
  },

  /**
   * Active users in this tenant who effectively hold `permission`.
   *
   * Readable by ANY authenticated user, unlike `getUsers` above, which is admin-only. A picker for
   * "who should sign this?" has to be the people who actually can — offering everybody means
   * assigning somebody who is then refused by the server, which teaches nobody anything until it
   * fails. Effective, so a per-user grant or revoke is honoured, and admins always appear because
   * they bypass permission checks everywhere else.
   */
  getUsersWithPermission: async (permission) => {
    const response = await client.get(`/user/by-permission/${encodeURIComponent(permission)}`)
    return response.data
  },

  /**
   * Delete user (admin only)
   * @param {number} userId - User ID
   * @returns {Promise}
   */
  deleteUser: async (userId) => {
    const response = await client.delete(`/user/${userId}`)
    return response.data
  },

  /**
   * Deactivate user account (prevent login). Admin only.
   * @param {string} userId - User ID
   * @returns {Promise}
   */
  deactivateUser: async (userId) => {
    const response = await client.put(`/user/${userId}/deactivate`)
    return response.data
  },

  /**
   * Reactivate user account (allow login). Admin only.
   * @param {string} userId - User ID
   * @returns {Promise}
   */
  reactivateUser: async (userId) => {
    const response = await client.put(`/user/${userId}/reactivate`)
    return response.data
  },

  /**
   * Toggle user active/inactive status. Admin only.
   * @param {string} userId - User ID
   * @returns {Promise} {message}
   */
  toggleUserActive: async (userId) => {
    const response = await client.patch(`/user/${userId}/toggle-active`)
    return response.data
  },

  /**
   * Update user role (admin only)
   * @param {number} userId - User ID
   * @param {string} role - New role
   * @returns {Promise} Updated user data
   */
  updateUserRole: async (userId, role) => {
    const response = await client.put(`/user/${userId}/role`, { role })
    return response.data
  },

  /**
   * Set WHICH LANGUAGE another user reads the application in (admin only).
   *
   * The onboarding path, and the one that matters: a person who does not read English cannot be
   * expected to find a settings screen written in English in order to say so. `language: null`
   * CLEARS the preference — which is not the same as choosing English, it hands the decision back to
   * that person's device and the company default.
   *
   * @param {string} userId
   * @param {string|null} language - a tag like 'es', or null to clear
   */
  setUserLanguage: async (userId, language) => {
    const response = await client.put(`/user/${userId}/language`, { language: language || null })
    return response.data
  },

  /**
   * The CURRENT user's saved language, or null for "never chosen".
   *
   * One field, from a route that returns only that field. It exists because an admin can set this
   * for somebody else (setUserLanguage above), and until something re-read it that write was
   * invisible to a session already open — `/auth/verify-token` answers `{valid, user_id}` and
   * nothing else. Kept separate from getProfile so a boot-time reconciliation can never merge a
   * whole user shape over a live session's role or permissions.
   */
  getMyLanguage: async () => {
    const response = await client.get('/user/me/language')
    return response.data
  },

  /**
   * Set the CURRENT user's own language. `null` clears it (see setUserLanguage).
   */
  setMyLanguage: async (language) => {
    const response = await client.put('/user/me/language', { language: language || null })
    return response.data
  },

  /**
   * Update user module access (admin only)
   * @param {number} userId - User ID
   * @param {array} accessibleModules - Array of accessible module IDs
   * @returns {Promise} Updated user data
   */
  updateUserModules: async (userId, accessibleModules) => {
    const response = await client.put(`/user/${userId}/modules`, { 
      accessible_modules: accessibleModules 
    })
    return response.data
  },

  // ==================== Users ====================

  /**
   * Get all users with estimator role
   * @returns {Promise} Array of estimator users
   */
  getEstimators: async () => {
    const response = await client.get('/user/estimators')
    return response.data
  },

  // ==================== Role Management ====================

  /**
   * Get all roles (admin only)
   * @returns {Promise} Array of roles
   */
  getRoles: async () => {
    const response = await client.get('/roles/')
    return response.data
  },

  /**
   * Create a new role (admin only)
   * @param {object} roleData - Role data
   * @returns {Promise} Created role
   */
  createRole: async (roleData) => {
    const response = await client.post('/roles/', roleData)
    return response.data
  },

  /**
   * Update a role (admin only)
   * @param {string} roleId - Role ID
   * @param {object} roleData - Role fields to update
   * @returns {Promise} Updated role
   */
  updateRole: async (roleId, roleData) => {
    const response = await client.put(`/roles/${roleId}`, roleData)
    return response.data
  },

  /**
   * Delete a role (admin only)
   * @param {string} roleId - Role ID
   * @returns {Promise}
   */
  deleteRole: async (roleId) => {
    const response = await client.delete(`/roles/${roleId}`)
    return response.data
  },

  // ==================== Permissions & Access Control ====================

  getUserPermissions: async (userId) => {
    const response = await client.get(`/user/${userId}/permissions`)
    return response.data
  },

  // `known` = every permission key the UI rendered, so the server only removes (denies) a
  // role-default among those actually shown — a permission the panel didn't display is never
  // silently dropped.
  setUserPermissions: async (userId, permissions, known) => {
    const response = await client.put(`/user/${userId}/permissions`, { permissions, known })
    return response.data
  },

  getUserAccessModes: async (userId) => {
    const response = await client.get(`/user/${userId}/access-modes`)
    return response.data
  },

  setUserAccessMode: async (userId, module, mode) => {
    const response = await client.put(`/user/${userId}/access-modes/${module}`, { mode })
    return response.data
  },

  getUserRecordGrants: async (userId, module) => {
    const response = await client.get(`/user/${userId}/record-grants/${module}`)
    return response.data
  },

  setUserRecordGrants: async (userId, module, recordIds) => {
    const response = await client.put(`/user/${userId}/record-grants/${module}`, { record_ids: recordIds })
    return response.data
  },

  // ==================== App Logo Management ====================

  /**
   * Return the URL for an app UI logo (no HTTP request — just builds the URL).
   * @param {'light'|'dark'|'icon'} type - 'light' nav/light-bg, 'dark' headers/login, 'icon' tab + home screen
   * @returns {string} Absolute URL to the logo endpoint
   */
  getAppLogoUrl: (type = 'light') => {
    const baseURL = client.defaults?.baseURL || '/api/identity'
    const domain = window.location.hostname
    return `${baseURL}/logo/${type}?domain=${encodeURIComponent(domain)}`
  },

  /**
   * Upload a new app UI logo (master admin only).
   * @param {'light'|'dark'|'icon'} type - Logo variant to replace
   * @param {File} file - Image file (PNG / JPG / WebP, max 10 MB)
   * @returns {Promise}
   */
  uploadAppLogo: async (type, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await client.post(`/logo/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getAppLogoFileUrl: (filename) => {
    const baseURL = client.defaults?.baseURL || '/api/identity'
    return `${baseURL}/logo/file/${encodeURIComponent(filename)}`
  },

  listAppLogos: async () => {
    const response = await client.get('/logo/library')
    return response.data
  },

  selectAppLogo: async (type, filename) => {
    const response = await client.put(`/logo/select/${type}`, { filename })
    return response.data
  },

  // ==================== Cross-tenant branding (system_admin) ====================

  /** Load a specific tenant's current branding (colors + logo presence) for the editor. */
  getBrandingAdmin: async (tenantId) => {
    const response = await client.get(`/branding/${tenantId}/admin`)
    return response.data
  },

  /** Build the logo URL for an ARBITRARY tenant host (super-admin preview). */
  getAppLogoUrlForDomain: (type, domain) => {
    const baseURL = client.defaults?.baseURL || '/api/identity'
    return `${baseURL}/logo/${type}?domain=${encodeURIComponent(domain || '')}`
  },

  /** Upload a logo FOR ANY tenant (system_admin only; the backend writes on the app session
   * under the target tenant's RLS context). */
  uploadAppLogoForTenant: async (tenantId, type, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await client.post(`/logo/${tenantId}/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  /** Select a library logo as ANY tenant's active logo (system_admin only). */
  selectAppLogoForTenant: async (tenantId, type, filename) => {
    const response = await client.put(`/logo/${tenantId}/select/${type}`, { filename })
    return response.data
  },

  // ==================== Health Check ====================

  /**
   * Check service health
   * @returns {Promise} Health status
   */
  healthCheck: async () => {
    const response = await client.get('/health')
    return response.data
  },
}
